import React from "react";
import { motion } from "framer-motion";
import { Check, Loader2, Sparkles, Star, Crown } from "lucide-react";
import { useCheckout } from "@/lib/checkout";

const plans = [
  {
    id: "basic",
    name: "BASIC",
    price: "$12.13",
    description: "Everything you need to get noticed.",
    Icon: Sparkles,
    cta: "Get Basic",
    popular: false,
    features: [
      "Professional AI Resume",
      "ATS Optimization",
      "Modern Design",
      "PDF + DOCX Delivery",
      "Delivered in minutes",
    ],
  },
  {
    id: "pro",
    name: "PRO",
    price: "$26.01",
    description: "The most chosen plan — best value for serious applicants.",
    Icon: Star,
    cta: "Get Pro",
    popular: true,
    features: [
      "Everything in BASIC",
      "In-depth AI analysis report",
      "ATS score before & after",
      "3 AI revisions included",
      "Recruiter-focused wording",
      "Keyword targeting for your role",
      "Premium modern layout",
    ],
  },
  {
    id: "elite",
    name: "ELITE",
    price: "$51.21",
    description: "End-to-end career rebrand for top-tier roles.",
    Icon: Crown,
    cta: "Get Elite",
    popular: false,
    features: [
      "Everything in PRO",
      "Job-specific resume targeting",
      "Interview preparation tips",
      "3 AI revisions included",
      "Ultra-premium design",
      "Personal branding optimization",
    ],
  },
];

export const Pricing = () => {
  const { startCheckout, loadingPlan } = useCheckout();
  return (
    <section
      id="pricing"
      className="relative py-24 sm:py-32"
      data-testid="pricing-section"
    >
      <div
        aria-hidden
        className="absolute left-1/2 -translate-x-1/2 top-1/3 h-[420px] w-[760px] rounded-full bg-gradient-to-r from-blue-600/15 to-purple-600/15 blur-[140px] pointer-events-none"
      />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto">
          <p className="text-xs uppercase tracking-[0.18em] text-zinc-500">
            Pricing
          </p>
          <h2
            className="mt-3 text-3xl md:text-4xl font-semibold tracking-tight text-white text-balance"
            data-testid="pricing-headline"
          >
            Choose the plan that fits your career.
          </h2>
          <p className="mt-4 text-zinc-400">
            One-time payment. No subscriptions. Each plan is crafted by AI and
            reviewed for impact.
          </p>
        </div>

        <p
          className="mx-auto mt-10 max-w-3xl text-center text-sm text-zinc-400 leading-relaxed text-balance"
          data-testid="pricing-anchor-line"
        >
          Traditional resume writers charge{" "}
          <span className="text-white">$300–$1,200</span> per document. We
          deliver the same outcome for a fraction of the cost — one-time
          payment, no subscription.
        </p>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6 items-stretch">
          {plans.map((plan, i) => (
            <PlanCard
              key={plan.id}
              plan={plan}
              index={i}
              onSelect={startCheckout}
              loading={loadingPlan === `cv_${plan.id}`}
            />
          ))}
        </div>

        <p className="mt-10 text-center text-xs text-zinc-500">
          Secure checkout · Powered by Stripe · Instant AI delivery · Questions?{" "}
          <a
            href="mailto:getcvaipro@gmail.com?subject=Pre-sale%20question"
            className="text-zinc-300 hover:text-white underline underline-offset-2"
            data-testid="pricing-contact-email"
          >
            getcvaipro@gmail.com
          </a>
        </p>
      </div>
    </section>
  );
};

const PlanCard = ({ plan, index, onSelect, loading }) => {
  const { name, price, description, Icon, cta, popular, features } = plan;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.5, delay: index * 0.08 }}
      className={`group relative ${popular ? "md:scale-100 lg:-translate-y-2" : ""}`}
      data-testid={`pricing-card-${plan.id}`}
    >
      {/* Animated gradient border for popular plan */}
      {popular && (
        <div
          aria-hidden
          className="absolute -inset-px rounded-3xl bg-gradient-to-r from-blue-500/70 via-indigo-500/50 to-purple-500/70 border-shimmer"
        />
      )}
      {/* Subtle hover-only border glow for non-popular plans */}
      {!popular && (
        <div
          aria-hidden
          className="absolute -inset-px rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
          style={{
            background:
              "linear-gradient(135deg, rgba(59,130,246,0.25), rgba(139,92,246,0.25))",
          }}
        />
      )}

      <div
        className={`relative h-full rounded-3xl border ${
          popular
            ? "border-white/5 bg-gradient-to-b from-zinc-950 to-black"
            : "border-white/10 bg-zinc-950/60"
        } p-7 sm:p-8 flex flex-col transition-transform duration-500 group-hover:-translate-y-0.5`}
      >
        {/* Top row */}
        <div className="flex items-center justify-between">
          <div className="inline-flex items-center gap-2">
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg border border-white/10 bg-gradient-to-br from-zinc-900 to-black">
              <Icon className="h-3.5 w-3.5 text-white/90" />
            </span>
            <h3 className="text-sm font-medium tracking-wide text-white">
              {name}
            </h3>
          </div>
          {popular ? (
            <span
              className="inline-flex items-center gap-1 rounded-full bg-white text-black px-2.5 py-1 text-[10px] font-medium tracking-wide"
              data-testid="pricing-popular-badge"
            >
              <Sparkles className="h-3 w-3" />
              MOST POPULAR
            </span>
          ) : (
            <span className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono">
              One-time
            </span>
          )}
        </div>

        {/* Price */}
        <div className="mt-6">
          <div className="flex items-baseline gap-2">
            <span
              className="text-4xl sm:text-5xl font-semibold tracking-tighter text-white"
              data-testid={`pricing-amount-${plan.id}`}
            >
              {price}
            </span>
            <span className="text-sm text-zinc-500">USD</span>
          </div>
          <p className="mt-2 text-sm text-zinc-400 min-h-[2.5rem]">
            {description}
          </p>
        </div>

        {/* Features */}
        <ul className="mt-7 space-y-3 flex-1">
          {features.map((f, idx) => (
            <li
              key={f}
              className="flex items-start gap-3 text-sm text-zinc-200"
              data-testid={`pricing-feature-${plan.id}-${idx}`}
            >
              <span
                className={`inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full mt-0.5 ${
                  popular
                    ? "bg-white/15 border border-white/30"
                    : "bg-emerald-500/15 border border-emerald-500/30"
                }`}
              >
                <Check
                  className={`h-3 w-3 ${popular ? "text-white" : "text-emerald-400"}`}
                />
              </span>
              <span className="leading-relaxed">{f}</span>
            </li>
          ))}
        </ul>

        {/* CTA */}
        <button
          onClick={() => onSelect(plan.id)}
          disabled={loading}
          className={`mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-medium transition-colors disabled:opacity-60 ${
            popular
              ? "bg-white text-black hover:bg-zinc-200"
              : "bg-white/10 text-white border border-white/15 hover:bg-white/15"
          }`}
          data-testid={`pricing-cta-${plan.id}`}
        >
          {loading && <Loader2 className="h-4 w-4 animate-spin" />}
          {cta}
        </button>
      </div>
    </motion.div>
  );
};
